'use client';

import { useMemo, type ComponentType } from 'react';
import { useTranslations } from 'next-intl';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Box } from '@terra/ui-styled-system';
import {
  GroupHeaderCell,
  isFindingsGroupHeaderRow,
  type FindingsGridRow,
  type InternalEnrichedFinding,
} from '@terra/ui-design-system';
import type { FindingsGroupByField } from '@terra/shared-contracts-findings';
import { rowClickBlockerProps } from '../rowClickBlocker';
import { useFindingsTableContext } from './FindingsTableContext';
import {
  FindingsTableSelectAllHeader,
  FindingsTableSelectionCell,
} from './FindingsTableSelectionCells';
import {
  FindingActionsCell,
  FindingCategoryCell,
  FindingDateCell,
  FindingPublishedCell,
  FindingRelatedAssetCell,
  FindingRelatedSignalCell,
  FindingRetestedCell,
  FindingSeverityCell,
  FindingStatusIconCell,
  FindingTitleCell,
} from './FindingsTableCells';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import {
  isFindingsColumnSortableInView,
  type FindingsSortableColumn,
} from './findings-list.model';

type FindingsDataColumn = {
  colId: FindingsSortableColumn | 'retested';
  header: string;
  width?: number;
  flex?: number;
  minWidth: number;
  justify?: 'center';
  allowOverflow?: boolean;
  Cell: ComponentType<{ finding: InternalEnrichedFinding }>;
};

const SELECT_COLUMN_WIDTH = 40;
const ACTIONS_COLUMN_WIDTH = 48;
const preserveServerSortOrder = () => 0;

const renderFindingCell =
  ({
    Cell,
    justify,
    allowOverflow,
  }: Pick<FindingsDataColumn, 'Cell' | 'justify'> & {
    allowOverflow?: boolean;
  }) =>
  (params: ICellRendererParams<FindingsGridRow>) => {
    if (!params.data || isFindingsGroupHeaderRow(params.data)) {
      return null;
    }

    return (
      <Box
        h="full"
        display="flex"
        alignItems="center"
        justifyContent={justify}
        minW={allowOverflow ? undefined : 0}
        overflow={allowOverflow ? 'visible' : undefined}
      >
        <Cell finding={params.data} />
      </Box>
    );
  };

export function useFindingsTableColumnDefs({
  groupByField,
}: {
  groupByField: FindingsGroupByField | null;
}): ColDef<FindingsGridRow>[] {
  const t = useTranslations('designSystem.findingsList.table');
  const tFindings = useTranslations('proxy.findings.table');
  const { isFeatureFlagEnabled: isSeverityChangeRequestEnabled } =
    useFeatureFlags('SEVERITY_CHANGE_REQUEST_ENABLED');
  const { collapsedGroupKeys, onToggleGroupCollapsed } =
    useFindingsTableContext();

  return useMemo(() => {
    const columns: FindingsDataColumn[] = [
      {
        colId: 'status',
        header: t('status'),
        width: 140,
        minWidth: 120,
        Cell: FindingStatusIconCell,
      },
      {
        colId: 'severity',
        header: t('severity'),
        ...(isSeverityChangeRequestEnabled
          ? {
              width: 180,
              minWidth: 160,
              allowOverflow: true,
            }
          : {
              width: 44,
              minWidth: 36,
            }),
        Cell: FindingSeverityCell,
      },
      {
        colId: 'title',
        header: t('title'),
        flex: 1,
        minWidth: 240,
        Cell: FindingTitleCell,
      },
      {
        colId: 'category',
        header: t('category'),
        width: 160,
        minWidth: 120,
        Cell: FindingCategoryCell,
      },
      {
        colId: 'assets',
        header: tFindings('relatedAsset'),
        width: 240,
        minWidth: 160,
        Cell: FindingRelatedAssetCell,
      },
      {
        colId: 'relatedSignal',
        header: t('relatedSignal'),
        width: 200,
        minWidth: 160,
        Cell: FindingRelatedSignalCell,
      },
      {
        colId: 'isPublished',
        header: t('published'),
        width: 140,
        minWidth: 120,
        Cell: FindingPublishedCell,
      },
      {
        colId: 'retested',
        header: t('retested'),
        width: 96,
        minWidth: 88,
        Cell: FindingRetestedCell,
      },
      {
        colId: 'createdAt',
        header: t('created'),
        width: 160,
        minWidth: 140,
        Cell: ({ finding }) => (
          <FindingDateCell finding={finding} field="createdAt" />
        ),
      },
      {
        colId: 'updatedAt',
        header: t('lastUpdated'),
        width: 160,
        minWidth: 140,
        Cell: ({ finding }) => (
          <FindingDateCell finding={finding} field="updatedAt" />
        ),
      },
    ];
    const dataColumns = columns.map<ColDef<FindingsGridRow>>(
      (column, index) => ({
        colId: column.colId,
        headerName: column.header,
        width: column.width,
        flex: column.flex,
        minWidth: column.minWidth,
        sortable: isFindingsColumnSortableInView({
          colId: column.colId,
          groupByField,
        }),
        lockPosition: index === 0 ? 'left' : undefined,
        comparator: preserveServerSortOrder,
        colSpan: (params) =>
          index === 0 && isFindingsGroupHeaderRow(params.data)
            ? columns.length
            : 1,
        cellRenderer:
          index === 0
            ? (params: ICellRendererParams<FindingsGridRow>) =>
                isFindingsGroupHeaderRow(params.data) ? (
                  <GroupHeaderCell
                    group={params.data}
                    isCollapsed={collapsedGroupKeys.has(params.data.groupKey)}
                    onToggle={onToggleGroupCollapsed}
                    containerProps={rowClickBlockerProps}
                  />
                ) : (
                  renderFindingCell(column)(params)
                )
            : renderFindingCell(column),
        cellStyle: column.allowOverflow ? { overflow: 'visible' } : undefined,
      }),
    );

    return [
      {
        colId: 'select',
        headerName: '',
        width: SELECT_COLUMN_WIDTH,
        minWidth: SELECT_COLUMN_WIDTH,
        maxWidth: SELECT_COLUMN_WIDTH,
        pinned: 'left',
        resizable: false,
        sortable: false,
        suppressMovable: true,
        suppressNavigable: true,
        headerComponent: FindingsTableSelectAllHeader,
        cellRenderer: (params: ICellRendererParams<FindingsGridRow>) => (
          <FindingsTableSelectionCell row={params.data} />
        ),
      },
      ...dataColumns,
      {
        colId: 'actions',
        headerName: '',
        width: ACTIONS_COLUMN_WIDTH,
        minWidth: ACTIONS_COLUMN_WIDTH,
        maxWidth: ACTIONS_COLUMN_WIDTH,
        pinned: 'right',
        resizable: false,
        sortable: false,
        suppressMovable: true,
        suppressNavigable: true,
        cellRenderer: renderFindingCell({
          Cell: FindingActionsCell,
          justify: 'center',
        }),
      },
    ];
  }, [
    collapsedGroupKeys,
    groupByField,
    isSeverityChangeRequestEnabled,
    onToggleGroupCollapsed,
    t,
    tFindings,
  ]);
}
